import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Kursinhalt', id: 'course-features' },
  { label: 'Über uns', id: 'about' },
  { label: 'Kurse', id: 'current-courses' },
  { label: 'Bewertungen', id: 'testimonials' },
  { label: 'FAQ', id: 'faq' },
  { label: 'Kontakt', id: 'contact' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 8);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const handleNavClick = (e: React.MouseEvent, id: string) => {
    if (location.pathname !== '/') return;
    e.preventDefault();
    setIsMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/[0.97] backdrop-blur-xl'
          : 'bg-white/[0.85] backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center h-16">

          {/* Logo */}
          <Link
            to="/"
            onClick={() => {
              if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-2.5 shrink-0"
          >
            <img
              src="/LogoHeader4.png"
              alt="Arabicroots"
              className="h-9 w-auto object-contain"
            />
          </Link>

          {/* Nav — absolutely centred */}
          <nav className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center">
            {NAV_LINKS.map(({ label, id }) => (
              <a
                key={id}
                href={location.pathname === '/' ? `#${id}` : `/#${id}`}
                onClick={e => handleNavClick(e, id)}
                className="px-3 py-2 text-[13px] font-medium text-gray-600 hover:text-gray-900 transition-colors duration-150"
              >
                {label}
              </a>
            ))}
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-2 ml-auto shrink-0">
            <Link
              to="/login"
              style={{ transition: 'border-color .15s, color .15s, transform .15s' }}
              className="px-3.5 py-1.5 rounded-full border border-gray-200 text-[13px] font-medium text-gray-700"
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#970000'; e.currentTarget.style.color = '#970000'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; e.currentTarget.style.transform = ''; }}
            >
              Schülerlogin
            </Link>
            <Link
              to="/anmelden"
              style={{ transition: 'filter .15s, transform .15s', background: 'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15)), #b30000' }}
              className="px-3.5 py-1.5 rounded-full text-[13px] font-semibold text-white"
              onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}
            >
              Anmelden
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden ml-auto p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            onClick={() => setIsMenuOpen(v => !v)}
            aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="lg:hidden py-2 border-t border-gray-100 overflow-hidden"
          >
            <div className="flex flex-col gap-0.5">
              {NAV_LINKS.map(({ label, id }) => (
                <a
                  key={id}
                  href={location.pathname === '/' ? `#${id}` : `/#${id}`}
                  onClick={e => handleNavClick(e, id)}
                  className="px-3 py-2.5 rounded-lg text-[14px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  {label}
                </a>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100 flex flex-col gap-2">
              <Link
                to="/login"
                className="flex items-center justify-center py-2.5 rounded-full border border-gray-200 text-[14px] font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Schülerlogin
              </Link>
              <Link
                to="/anmelden"
                className="relative overflow-hidden flex items-center justify-center py-2.5 rounded-full text-[14px] font-semibold text-white transition-colors"
                style={{ background: 'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15)), #b30000' }}
              >
                Anmelden
              </Link>
            </div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
